"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isRecord = isRecord;
exports.ownLookup = ownLookup;
exports.toJsonValue = toJsonValue;
exports.optionalString = optionalString;
exports.nullableString = nullableString;
exports.parseIfString = parseIfString;
exports.asRecord = asRecord;
exports.parseRecord = parseRecord;
exports.parseArray = parseArray;
exports.toProviderMetadata = toProviderMetadata;
exports.remainingProviderMetadata = remainingProviderMetadata;
exports.compact = compact;
exports.omitKeys = omitKeys;
exports.recordKeyAsParsed = recordKeyAsParsed;
/**
 * Generic JSON/string utilities shared by the parser and the convention
 * modules. No provider knowledge lives here.
 */
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
 * Own-property lookup for input-derived keys: `__proto__`/`constructor` must
 * miss (taking the caller's unknown-value fallback), not resolve through the
 * prototype chain to non-handler values.
 */
function ownLookup(record, key) {
    return record !== undefined && Object.hasOwn(record, key)
        ? record[key]
        : undefined;
}
function toJsonValue(value) {
    if (value === null)
        return null;
    if (typeof value === "string" || typeof value === "boolean")
        return value;
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : String(value);
    }
    if (typeof value === "bigint")
        return value.toString();
    if (Array.isArray(value))
        return value.map(toJsonValue);
    if (isRecord(value)) {
        return Object.fromEntries(Object.entries(value).map(([key, nestedValue]) => [
            key,
            toJsonValue(nestedValue),
        ]));
    }
    // Telemetry values should already be JSON-compatible. Preserve an explicit
    // fallback instead of dropping an unexpected value from the custom part.
    return value === undefined ? null : String(value);
}
function optionalString(value) {
    return typeof value === "string" && value.length > 0 ? value : undefined;
}
function nullableString(value) {
    return typeof value === "string" && value.length > 0 ? value : null;
}
/** Parse one JSON-string boundary. Nested values are parsed only by their owner. */
function parseIfString(value) {
    if (typeof value !== "string")
        return value;
    try {
        return JSON.parse(value);
    }
    catch {
        return value;
    }
}
function asRecord(value) {
    return isRecord(value) ? value : undefined;
}
function parseRecord(value) {
    return asRecord(parseIfString(value));
}
function parseArray(value) {
    const parsed = parseIfString(value);
    return Array.isArray(parsed) ? parsed : undefined;
}
function toProviderMetadata(entries) {
    const value = toJsonValue(entries);
    return isRecord(value) && Object.keys(value).length > 0 ? value : undefined;
}
/**
 * Preserve the fields a normalizer did not consume. Records are folded in
 * order and explicit metadata wins last, matching object-spread semantics
 * without requiring every provider handler to rebuild the same remainder.
 */
function remainingProviderMetadata(records, consumedKeys, explicitMetadata) {
    const remaining = {};
    for (const record of records) {
        for (const [key, value] of Object.entries(record)) {
            if (!consumedKeys.has(key))
                remaining[key] = value;
        }
    }
    if (explicitMetadata) {
        for (const [key, value] of Object.entries(explicitMetadata)) {
            remaining[key] = value;
        }
    }
    return toProviderMetadata(remaining);
}
/** Strip undefined-valued keys so optional fields are absent, not undefined. */
function compact(obj) {
    return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));
}
function omitKeys(record, keys) {
    return Object.fromEntries(Object.entries(record).filter(([key]) => !keys.includes(key)));
}
function recordKeyAsParsed(unparsedKeys, key, ...nestedPath) {
    if (nestedPath.length === 0) {
        const value = unparsedKeys[key];
        delete unparsedKeys[key];
        return value;
    }
    const nested = asRecord(unparsedKeys[key]);
    const [nestedKey, ...rest] = nestedPath;
    if (!nested || !(nestedKey in nested))
        return undefined;
    const nestedCopy = { ...nested };
    const value = recordKeyAsParsed(nestedCopy, nestedKey, ...rest);
    if (Object.keys(nestedCopy).length === 0) {
        delete unparsedKeys[key];
    }
    else {
        unparsedKeys[key] = nestedCopy;
    }
    return value;
}
